import React, { useEffect, useState } from "react";
import {
  getAllDocsSnap,
  setDocData,
} from "../../../utils/firebase/Firebase.utils";
import {
  FiBell,
  FiClock,
  FiCheck,
  FiFilter,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import ModalVariant1 from "../../../components/ModalVariant1";
import { adminEmailAddress } from "../../../utils/constants";

export default function NotificationWeb() {
  const currentUser = useSelector((state) => state.userObject);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState("unread"); // Default filter is unread
  const [filteredNotifications, setFilteredNotifications] = useState([]);
  const [markingAll, setMarkingAll] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [paginatedNotifications, setPaginatedNotifications] = useState([]);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchNotifications = async () => {
      await getAllDocsSnap("notifications", (docs) => {
        const adminNotifications = docs.filter((doc) =>
          currentUser.roles.includes("finance")
            ? doc.to === currentUser.email
            : doc.to === adminEmailAddress
        );
        // Sort notifications by dateTime in descending order
        adminNotifications.sort((a, b) => {
          return b.dateTime?.toDate() - a.dateTime?.toDate();
        });
        setNotifications(adminNotifications);
        setLoading(false);
      });
    };
    fetchNotifications();
  }, []);

  // Filter notifications whenever the active filter or notifications change
  useEffect(() => {
    if (activeFilter === "all") {
      setFilteredNotifications(notifications);
    } else if (activeFilter === "read") {
      setFilteredNotifications(
        notifications.filter((notification) => notification.read)
      );
    } else if (activeFilter === "unread") {
      setFilteredNotifications(
        notifications.filter((notification) => !notification.read)
      );
    }

    // Reset to first page when filter changes
    setCurrentPage(1);
  }, [activeFilter, notifications]);

  // Handle pagination
  useEffect(() => {
    const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
    setTotalPages(totalPages || 1);

    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    setPaginatedNotifications(
      filteredNotifications.slice(startIndex, endIndex)
    );
  }, [filteredNotifications, currentPage, itemsPerPage]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleNotificationClick = (notification) => {
    setSelectedNotification(notification);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedNotification(null);
  };

  const toggleReadStatus = async (e, notification) => {
    e.stopPropagation(); // Prevent the card click from triggering

    // Toggle the read status
    const newReadStatus = !notification.read;

    // Update in Firestore
    await setDocData("notifications", notification.id, {
      ...notification,
      read: newReadStatus,
    });

    // Update local state
    setNotifications(
      notifications.map((n) =>
        n.id === notification.id ? { ...n, read: newReadStatus } : n
      )
    );
  };

  const markAllAsRead = async () => {
    try {
      setMarkingAll(true);
      const unreadNotifications = notifications.filter((n) => !n.read);

      // Update each unread notification
      for (const notification of unreadNotifications) {
        await setDocData("notifications", notification.id, {
          ...notification,
          read: true,
        });
      }

      // Update local state
      setNotifications(
        notifications.map((n) => (n.read ? n : { ...n, read: true }))
      );

      setMarkingAll(false);
    } catch (error) {
      console.error("Error marking all as read:", error);
      setMarkingAll(false);
    }
  };

  useEffect(() => {
    const updateReadStatus = async () => {
      if (isModalOpen && selectedNotification && !selectedNotification.read) {
        // Update the notification as read in Firestore
        await setDocData("notifications", selectedNotification.id, {
          ...selectedNotification,
          read: true,
        });
        // Update local state
        setNotifications(
          notifications.map((n) =>
            n.id === selectedNotification.id ? { ...n, read: true } : n
          )
        );
      }
    };
    updateReadStatus();
  }, [isModalOpen, selectedNotification]);

  if (loading) {
    return (
      <div className="text-gray-600 text-xs text-left max-w-7xl mx-auto mt-8 p-4">
        <div className="header text-lg font-semibold mb-4 text-[#363062]">
          Your Notifications
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-[#8FCD00] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  const unreadCount = notifications.filter((n) => !n.read).length;

  // Pagination UI component
  const PaginationControls = () => {
    if (totalPages <= 1) return null;

    return (
      <div className="flex items-center justify-between mt-6 relative px-2">
        <button
          onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="p-2 rounded-md bg-[#363062] text-white disabled:opacity-50 disabled:cursor-not-allowed absolute left-0"
        >
          <FiChevronLeft size={16} />
        </button>

        <div className="flex items-center space-x-1 mx-auto">
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter((page) => {
              // Show first page, last page, current page, and pages around current page
              return (
                page === 1 ||
                page === totalPages ||
                Math.abs(page - currentPage) <= 1
              );
            })
            .map((page, index, array) => {
              // Add ellipsis where there are gaps
              const showEllipsisBefore =
                index > 0 && array[index - 1] !== page - 1;

              return (
                <React.Fragment key={page}>
                  {showEllipsisBefore && (
                    <span className="px-3 py-1 text-gray-500">...</span>
                  )}
                  <button
                    onClick={() => handlePageChange(page)}
                    className={`w-8 h-8 flex items-center justify-center rounded-md text-xs ${
                      currentPage === page
                        ? "bg-[#8FCD00] text-white font-medium"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    {page}
                  </button>
                </React.Fragment>
              );
            })}
        </div>

        <button
          onClick={() =>
            handlePageChange(Math.min(totalPages, currentPage + 1))
          }
          disabled={currentPage === totalPages}
          className="p-2 rounded-md bg-[#363062] text-white disabled:opacity-50 disabled:cursor-not-allowed absolute right-0"
        >
          <FiChevronRight size={16} />
        </button>
      </div>
    );
  };

  return (
    <div className="text-gray-600 text-xs text-left max-w-7xl mx-auto mt-8 p-4">
      <div className="flex justify-between items-center mb-6">
        <div className="header text-lg font-semibold text-[#363062]">
          Your Notifications
        </div>

        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            disabled={markingAll}
            className="flex items-center gap-2 px-4 py-2 bg-[#363062] text-white rounded-md text-xs hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FiCheck className="w-4 h-4" />
            {markingAll ? "Processing..." : "Mark All as Read"}
          </button>
        )}
      </div>

      {/* Filter Tabs */}
      <div className="flex space-x-1 mb-6 border-b border-gray-200">
        {["all", "unread", "read"].map((filter) => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={`px-4 py-2 text-sm font-medium capitalize transition-colors duration-200 flex items-center gap-2
              ${
                activeFilter === filter
                  ? "text-[#363062] border-b-2 border-[#8FCD00]"
                  : "text-gray-500 hover:text-[#363062]"
              }`}
          >
            {filter === "all" && <FiFilter className="w-4 h-4" />}
            {filter === "unread" && <FiBell className="w-4 h-4" />}
            {filter === "read" && <FiCheck className="w-4 h-4" />}
            {filter}
            {filter !== "all" && (
              <span className="ml-1 text-xs bg-gray-100 px-2 py-0.5 rounded-full">
                {
                  notifications.filter((n) =>
                    filter === "read" ? n.read : !n.read
                  ).length
                }
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Showing entries information */}
      {filteredNotifications.length > 0 && (
        <div className="text-xs text-gray-500 mb-4">
          Showing{" "}
          {Math.min(
            (currentPage - 1) * itemsPerPage + 1,
            filteredNotifications.length
          )}{" "}
          to{" "}
          {Math.min(currentPage * itemsPerPage, filteredNotifications.length)}{" "}
          of {filteredNotifications.length} entries
        </div>
      )}

      {filteredNotifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500 text-sm">
          <FiBell className="w-12 h-12 mb-4 text-[#363062] opacity-30" />
          <p>
            You have no {activeFilter !== "all" ? activeFilter : "new"}{" "}
            notifications
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {paginatedNotifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={`p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer
                ${
                  notification.read
                    ? "border-l-4 border-gray-200"
                    : "border-l-4 border-[#8FCD00]"
                }`}
            >
              <div className="flex items-start gap-4">
                {/* Notification Icon */}
                <div
                  className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
                  ${notification.read ? "bg-gray-100" : "bg-[#363062]/10"}`}
                >
                  <FiBell
                    className={`w-5 h-5 ${
                      notification.read ? "text-gray-400" : "text-[#363062]"
                    }`}
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-2">
                    <h3
                      className={`font-semibold text-sm ${
                        notification.read ? "text-gray-700" : "text-[#363062]"
                      }`}
                    >
                      {notification.subject}
                    </h3>
                    <div className="flex items-center">
                      {!notification.read && (
                        <span className="ml-2 px-2 py-0.5 text-xs font-medium text-[#363062] bg-[#363062]/10 rounded-full mr-2">
                          New
                        </span>
                      )}
                      <button
                        onClick={(e) => toggleReadStatus(e, notification)}
                        className={`flex items-center px-2 py-1 rounded text-xs font-medium transition-colors
                          ${
                            notification.read
                              ? "bg-gray-100 text-gray-600 hover:bg-gray-200"
                              : "bg-[#363062]/10 text-[#363062] hover:bg-[#363062]/20"
                          }`}
                      >
                        {notification.read ? "Mark as unread" : "Mark as read"}
                      </button>
                    </div>
                  </div>
                  <div className="text-gray-600 text-sm leading-relaxed line-clamp-2">
                    {notification.message}
                  </div>
                  <div className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                    <FiClock className="w-4 h-4" />
                    {notification.dateTime?.toDate().toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination controls */}
      <PaginationControls />

      {/* Modal */}
      <ModalVariant1
        isOpen={isModalOpen}
        onClose={closeModal}
        notification={selectedNotification}
      />
    </div>
  );
}
